import React, { useCallback, useMemo } from 'react';

import { Box, Button, Typography } from '@mui/material';
import { PlayIcon } from '@sharedCR/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { resolveColor } from 'constants/colors';
import { getIconComponentByValue } from 'constants/iconOptions';

import { ConfirmationViewTestIds } from './ConfirmationView.testIds';
import {
  cancelButtonSx,
  confirmationActionNameSx,
  confirmationButtonsSx,
  confirmationContainerSx,
  confirmationDefaultIconSx,
  confirmationIconSx,
} from './MobileLoanActionsDrawer.styles';
import { getIconBackground } from './mobileLoanActionsDrawer.utils';

const ConfirmationView = ({ actionData, onCancel, onProceed }) => {
  const IconComponent = useMemo(() => getIconComponentByValue(actionData.icon), [actionData.icon]);

  const resolvedColor = useMemo(() => resolveColor(actionData.color), [actionData.color]);

  const iconBg = useMemo(
    () => getIconBackground({ resolvedColor, variant: actionData.variant }),
    [resolvedColor, actionData.variant],
  );

  const hasCustomColor = Object.keys(iconBg).length > 0;

  const mergedIconSx = useMemo(() => {
    if (hasCustomColor) {
      return { ...confirmationIconSx, ...iconBg };
    }

    return confirmationDefaultIconSx;
  }, [iconBg, hasCustomColor]);

  const handleProceed = useCallback(() => {
    onProceed(actionData.id);
  }, [actionData.id, onProceed]);

  return (
    <Box sx={confirmationContainerSx}>
      <Box sx={mergedIconSx}>{IconComponent ? <IconComponent /> : <PlayIcon />}</Box>
      <Typography sx={confirmationActionNameSx} variant="bold16">
        {actionData.name}
      </Typography>
      <Typography color="text.secondary" variant="regular14">
        Do you want to proceed with this action?
      </Typography>
      <Box sx={confirmationButtonsSx}>
        <Button
          fullWidth
          color="primary"
          data-testid={ConfirmationViewTestIds.PROCEED_BUTTON}
          size="large"
          variant="contained"
          onClick={handleProceed}
        >
          Proceed
        </Button>
        <Button
          disableElevation
          fullWidth
          color="inherit"
          data-testid={ConfirmationViewTestIds.CANCEL_BUTTON}
          size="large"
          sx={cancelButtonSx}
          variant="contained"
          onClick={onCancel}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

ConfirmationView.propTypes = {
  actionData: PropTypes.object.isRequired,
  onCancel: PropTypes.func.isRequired,
  onProceed: PropTypes.func.isRequired,
};

export default ConfirmationView;
